import { useState, useEffect, useCallback } from '@wordpress/element';

const API_URL = '/wp-json/cqvs/v1/financing/calculate';

function formatCurrency(value) {
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function formatPercent(value) {
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + ' %';
}

export default function Financing({ price }) {
  const [isOpen, setIsOpen] = useState(false);
  const [financingType, setFinancingType] = useState('STANDARD_RATENKREDIT');
  const [amount, setAmount] = useState(price);
  const [downPayment, setDownPayment] = useState(0);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchCalculation = useCallback(async (type, amt, dp) => {
    setIsLoading(true);
    setError(null);

    const netAmount = amt - dp;
    if (netAmount <= 0) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    try {
      const body = {
        financingType: type,
        amount: amt,
        initialPayment: dp,
        insurance: false,
      };

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Berechnung fehlgeschlagen');
      }

      const data = await response.json();
      setResults(data);
      setHasFetched(true);

      if (data.length > 0 && !selectedDuration) {
        setSelectedDuration(data[0].duration);
      }
    } catch (err) {
      console.error('Financing API error:', err);
      setError('Finanzierungsberechnung konnte nicht geladen werden.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDuration]);

  useEffect(() => {
    if (isOpen && !hasFetched) {
      fetchCalculation(financingType, amount, downPayment);
    }
  }, [isOpen]);

  const handleRecalculate = () => {
    setSelectedDuration(null);
    fetchCalculation(financingType, amount, downPayment);
  };

  const handleTypeChange = (type) => {
    setFinancingType(type);
    setSelectedDuration(null);
    fetchCalculation(type, amount, downPayment);
  };

  const selected = results.find(r => r.duration === selectedDuration) || results[0] || null;

  const legalText = 'Die Angaben stellen zugleich das 2/3-Beispiel gemäß PAngV dar. Finanzierungsbeispiele der CreditPlus Bank AG, Augustenstraße 7, 70178 Stuttgart. Bonität vorausgesetzt. Die abgebildeten Finanzierungsbeispiele können sich je nach Aus- oder Abwahl zusätzlicher Versicherungsprodukte verändern.Für weitere Details und zusätzliche Kalkulationsmöglichkeiten sprechen Sie bitte Ihren Händler vor Ort an.';

  return (
    <div className="financing-accordion">
      <button
        className={`financing-accordion-header ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>Finanzierung</span>
        <svg className="financing-accordion-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="financing-accordion-body">
          <div className="financing-tabs">
            <button
              className={`financing-tab ${financingType === 'STANDARD_RATENKREDIT' ? 'active' : ''}`}
              onClick={() => handleTypeChange('STANDARD_RATENKREDIT')}
            >
              Lineare Finanzierung
            </button>
            <button
              className={`financing-tab ${financingType === 'BALLON_FINANZIERUNG' ? 'active' : ''}`}
              onClick={() => handleTypeChange('BALLON_FINANZIERUNG')}
            >
              Ballon-Finanzierung
            </button>
          </div>

          <div className="financing-inputs">
            <div className="financing-field">
              <label>Unverbindliche Preisempfehlung</label>
              <div className="financing-input-wrap">
                <span className="financing-input-prefix">€</span>
                <input
                  type="text"
                  value={formatCurrency(amount)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value.replace(/\./g, '').replace(',', '.'));
                    if (!isNaN(val)) setAmount(val);
                  }}
                  onBlur={handleRecalculate}
                />
              </div>
            </div>

            <div className="financing-field">
              <label>Anzahlung</label>
              <div className="financing-input-wrap">
                <span className="financing-input-prefix">€</span>
                <input
                  type="text"
                  value={formatCurrency(downPayment)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value.replace(/\./g, '').replace(',', '.'));
                    if (!isNaN(val)) setDownPayment(val);
                  }}
                  onBlur={handleRecalculate}
                />
              </div>
            </div>

            <div className="financing-field">
              <label>Laufzeit</label>
              <select
                value={selectedDuration || ''}
                onChange={(e) => setSelectedDuration(parseInt(e.target.value))}
                disabled={results.length === 0}
              >
                {results.map(r => (
                  <option key={r.duration} value={r.duration}>{r.duration} Monate</option>
                ))}
              </select>
            </div>
          </div>

          {isLoading && <div className="financing-loading">Berechnung läuft...</div>}
          {error && <div className="financing-error">{error}</div>}

          {selected && !isLoading && (
            <div className="financing-result">
              <div className="financing-rate">
                <span className="financing-rate-label">Monatliche Rate</span>
                <span className="financing-rate-value">€{formatCurrency(selected.rate)}</span>
              </div>

              <div className="financing-details">
                <div className="financing-detail-row">
                  <span>Fester Sollzinssatz p.a.</span>
                  <span>{formatPercent(selected.nominalInterest)}</span>
                </div>
                <div className="financing-detail-row">
                  <span>Effektiver Jahreszins</span>
                  <span>{formatPercent(selected.interest)}</span>
                </div>
                <div className="financing-detail-row">
                  <span>Laufzeit in Monaten</span>
                  <span>{selected.duration}</span>
                </div>
                {selected.lastRate != null && selected.lastRate !== selected.rate && (
                  <>
                    <div className="financing-detail-row">
                      <span>{selected.rateLength} Raten á</span>
                      <span>{formatCurrency(selected.rate)} €</span>
                    </div>
                    <div className="financing-detail-row">
                      <span>Schlussrate</span>
                      <span>{formatCurrency(selected.lastRate)} €</span>
                    </div>
                  </>
                )}
                {(selected.lastRate == null || selected.lastRate === selected.rate) && (
                  <div className="financing-detail-row">
                    <span>{selected.rateLength} Raten á</span>
                    <span>{formatCurrency(selected.rate)} €</span>
                  </div>
                )}
                <div className="financing-detail-row">
                  <span>Nettodarlehensbetrag</span>
                  <span>{formatCurrency(selected.netAmount)} €</span>
                </div>
                <div className="financing-detail-row financing-detail-row-total">
                  <span>Gesamtbetrag</span>
                  <span>{formatCurrency(selected.grossAmount)} €</span>
                </div>
              </div>

              <p className="financing-legal">{legalText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
